import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import epsClient from 'enrollment-platform/api/eps.client'
import {
  BillingProfileRecord,
  BillingProfileStatsDto,
  BulkUploadResultDto,
  CreateBillingProfileRequestDto,
  GetBillingProfilesRequestDto,
  GetBillingProfilesResponseDto,
  GetBillingProfileStatsRequestDto,
  HubspotSyncResultDto,
  HubspotSyncSubmitDto,
  HubspotSyncSubmitResultDto,
  ToggleBillingProfileStatusRequestDto,
  UpdateBillingProfileRequestDto,
} from './dto/client-billing-profile.dto'
import {
  GetActivityLogResponseDto,
  GetBillingProfileActivityLogRequestDto,
} from './dto/activity-log.dto'

const BASE = '/client-billing-profile'

export const getBillingProfiles = async (
  params: GetBillingProfilesRequestDto = {}
): Promise<GetBillingProfilesResponseDto> => {
  const response = await apiClient.get<GetBillingProfilesResponseDto>(BASE, {
    params,
  })
  return response.data
}

/** The list endpoint caps `limit` at 200, so exports page through all results. */
const LIST_PAGE_LIMIT = 200

export const getAllBillingProfiles = async (
  filters: Omit<GetBillingProfilesRequestDto, 'page' | 'limit'> = {}
): Promise<BillingProfileRecord[]> => {
  const first = await getBillingProfiles({
    ...filters,
    page: 1,
    limit: LIST_PAGE_LIMIT,
  })
  const totalPages = Math.ceil(first.total / LIST_PAGE_LIMIT)
  if (totalPages <= 1) return first.data

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      getBillingProfiles({
        ...filters,
        page: i + 2,
        limit: LIST_PAGE_LIMIT,
      })
    )
  )
  return rest.reduce((acc, page) => acc.concat(page.data), [...first.data])
}

export const createBillingProfile = async (
  body: CreateBillingProfileRequestDto
): Promise<BillingProfileRecord> => {
  const response = await apiClient.post<BillingProfileRecord>(BASE, body)
  return response.data
}

export const updateBillingProfile = async (
  id: number,
  body: UpdateBillingProfileRequestDto
): Promise<BillingProfileRecord> => {
  const response = await apiClient.patch<BillingProfileRecord>(
    `${BASE}/${id}`,
    body
  )
  return response.data
}

export const toggleBillingProfileStatus = async (
  id: number,
  body: ToggleBillingProfileStatusRequestDto
): Promise<BillingProfileRecord> => {
  const response = await apiClient.patch<BillingProfileRecord>(
    `${BASE}/${id}/status`,
    body
  )
  return response.data
}

export const getBillingProfileStats = async (
  params: GetBillingProfileStatsRequestDto = {}
): Promise<BillingProfileStatsDto> => {
  const response = await apiClient.get<BillingProfileStatsDto>(
    `${BASE}/stats`,
    {
      params,
    }
  )
  return response.data
}

export const bulkUploadBillingProfiles = async (
  file: File
): Promise<BulkUploadResultDto> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<BulkUploadResultDto>(
    `${BASE}/bulk-upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return response.data
}

export const runHubspotSync = async (
  excludeTestClients = true,
  force = false
): Promise<HubspotSyncResultDto> => {
  const response = await apiClient.post<HubspotSyncResultDto>(
    `${BASE}/hubspot-sync/run`,
    {},
    {
      params: {
        ...(!excludeTestClients && { excludeTestClients: 'false' }),
        ...(force && { force: 'true' }),
      },
    }
  )
  return response.data
}

export const submitHubspotSync = async (
  body: HubspotSyncSubmitDto
): Promise<HubspotSyncSubmitResultDto> => {
  const response = await apiClient.post<HubspotSyncSubmitResultDto>(
    `${BASE}/hubspot-sync`,
    body
  )
  return response.data ?? { applied: [], failed: [] }
}

export const getBillingProfileActivityLog = async (
  params: GetBillingProfileActivityLogRequestDto
): Promise<GetActivityLogResponseDto> => {
  const urlParams = new URLSearchParams()

  urlParams.set('categories', 'EMAIL')
  urlParams.append('action[]', 'BILLING_PROFILE_CREATE')
  urlParams.append('action[]', 'BILLING_PROFILE_UPDATE')
  urlParams.append('action[]', 'BILLING_PROFILE_STATUS_CHANGE')

  if (params.page !== undefined) urlParams.set('page', String(params.page))
  if (params.limit !== undefined) urlParams.set('limit', String(params.limit))
  if (params.search) urlParams.set('search', params.search)
  if (params.dateFrom) urlParams.set('dateFrom', params.dateFrom)
  if (params.dateTo) urlParams.set('dateTo', params.dateTo)
  if (params.sort) urlParams.set('sort', params.sort)
  if (params.order) urlParams.set('order', params.order)
  params.actorIds?.forEach((id) => urlParams.append('actorIds[]', id))
  params.clientIds?.forEach((clientId) =>
    urlParams.append('clientIds[]', clientId)
  )
  params.targetIds?.forEach((targetId) =>
    urlParams.append('targetIds[]', targetId)
  )

  const response = await epsClient.get<GetActivityLogResponseDto>(
    `/activity-log?${urlParams.toString()}`
  )
  return response.data
}
